import type {
  CurrencyBalance,
  LineItemCreateRequestType,
  Product,
  Role,
  User,
  UserBalances,
} from 'payments-api/model';
import type { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import {
  useGetRoles,
  usePostProducts,
  usePostUsers,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { useToast } from 'components/ToastManager';
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import {
  currencyCodes,
  currencyPrecision as currencyPrecisionMap,
} from '@fragment/types/currencyCodes';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';
import { Toast } from '@fragment/ui/src/components/Toast';

import { AnimatedGradientBg } from './AnimatedGradientBg';
import { CreateProductModal } from './CreateProductModal';
import { CreateUserModal } from './CreateUserModal';
import { FormatBillingAmount } from './formatBillingAmount';
import { ItemColorSquare } from './ItemColorSquare';
import { PaymentsModal } from './PaymentsModal';
import { RoleColorDot } from './RoleColorDot';

/** Strip commas from a formatted amount string. */
const stripCommas = (s: string): string => s.replace(/,/g, '');

/** Add thousand-separator commas to the integer part of a numeric string. */
const addCommas = (intPart: string): string => {
  if (intPart.length <= 3) return intPart;
  const chars = intPart.split('');
  const result: string[] = [];
  chars.reverse().forEach((ch, i) => {
    if (i > 0 && i % 3 === 0) result.push(',');
    result.push(ch);
  });
  return result.reverse().join('');
};

/** Format an amount string as the user types: allow digits and one decimal point, with commas. */
const formatAmountInput = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  const intPart = addCommas(parts[0]);
  if (parts.length <= 1) return intPart;
  return `${intPart}.${parts.slice(1).join('').slice(0, 2)}`;
};

/** Convert a dollar amount string to integer cents string. */
const dollarsToCents = (dollars: string, precision: number): string => {
  const num = parseFloat(stripCommas(dollars));
  if (Number.isNaN(num)) return '0';
  return String(Math.round(num * 10 ** precision));
};

/** Convert integer cents string to dollar display string. */
const centsToDollars = (cents: string, precision: number): string => {
  const num = parseInt(cents, 10);
  if (Number.isNaN(num)) return '';
  return formatAmountInput((num / 10 ** precision).toFixed(precision));
};

export type ItemTableItem = {
  id: string;
  paid?: number;

  /** The type of the line item */
  type: LineItemCreateRequestType;
  /** ID of the product/catalog item */
  product_id: string;
  /** Amount in smallest currency unit (e.g., cents) — total due (quantity × unitPrice) */
  amount: string;
  currencyCode: CurrencyCode;
  /** Description of the line item */
  description: string;
  user_id: string;
  /** Quantity of items. Defaults to 1. */
  quantity?: number;
  /** Unit price in smallest currency unit. Defaults to amount. */
  unitPrice?: string;
};

const ItemRow = ({
  item,
  index,
  isLast,
  onEdit,
  userHovered,
  onItemClick,
}: {
  item: ItemTableItem;
  index: number;
  isLast: boolean;
  onEdit?: () => void;
  userHovered?: boolean;
  onItemClick?: (item: ItemTableItem) => void;
}) => {
  const amountNum = Number(item.amount);
  const quantity = item.quantity ?? 1;
  const unitPrice = item.unitPrice ? Number(item.unitPrice) : amountNum;
  const precision =
    currencyPrecisionMap[item.currencyCode] || 2;
  return (
    <div
      className={clsx(
        'group/item flex flex-row w-full items-stretch border-main-200',
        onEdit && 'hover:bg-negative',
        onEdit && userHovered && 'bg-negative',
        !isLast && 'border-b-[0.5px]'
      )}
      data-testid={`item-row-${index}`}
    >
      <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main font-FragmentMono text-xmd border-r-[0.5px] border-main-200 aspect-square">
        {item.type === 'payin' ? '↙' : ''}
      </div>
      <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main font-FragmentMono text-xmd border-r-[0.5px] border-main-200 aspect-square">
        {item.type === 'payout' ? '↗' : ''}
      </div>
      <div className="flex items-center gap-x-f1 px-[13.5px] min-w-0 overflow-hidden flex-1 basis-0 flex-[3]">
        <ItemColorSquare productId={item.product_id} />
        {onItemClick ? (
          <button
            type="button"
            className="text-main truncate text-left hover:underline cursor-pointer"
            onClick={() => onItemClick(item)}
          >
            {item.product_id || '—'}
          </button>
        ) : (
          <span className="text-main truncate text-left">
            {item.product_id || '—'}
          </span>
        )}
      </div>
      <div className="flex items-center justify-end px-[13.5px] min-w-0 flex-1 basis-0 flex-[0.5]">
        <span className="text-main-500 text-xmd">{quantity}</span>
      </div>
      <div className="flex items-center justify-end px-[13.5px] min-w-0 flex-1 basis-0">
        <FormatBillingAmount
          amount={unitPrice}
          currencyCode={item.currencyCode}
          currencyPrecision={precision}
          secondary
        />
      </div>
      <div className="relative flex flex-row justify-end items-center gap-x-f1 px-[13.5px] min-w-0 overflow-hidden flex-1 basis-0">
        <div className={clsx(onEdit && 'group-hover/item:invisible')}>
          <FormatBillingAmount
            amount={amountNum}
            currencyCode={item.currencyCode}
            currencyPrecision={precision}
          />
        </div>
        {onEdit && (
          <div className="absolute inset-0 hidden group-hover/item:flex flex-row justify-end items-center gap-x-f1 px-[13.5px]">
            <PrimaryButtonV3
              onClick={onEdit}
              data-testid={`item-row-${index}-edit`}
            >
              Edit
            </PrimaryButtonV3>
          </div>
        )}
      </div>
    </div>
  );
};

const UserGroup = ({
  userId,
  items,
  startIndex,
  balances,
  users,
  onRemove,
  onEdit,
  onUserClick,
  onItemClick,
  onAddForUser,
}: {
  userId: string;
  items: ItemTableItem[];
  startIndex: number;
  balances?: CurrencyBalance[];
  users?: User[];
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
  onUserClick?: (userId: string) => void;
  onItemClick?: (item: ItemTableItem) => void;
  onAddForUser?: (userId: string) => void;
}) => {
  const [isUserHovered, setIsUserHovered] = useState(false);
  const isEditable = !!(onRemove || onEdit);
  const user = users?.find((u) => u.external_id === userId);
  return (
    <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
      <div
        className={clsx(
          kLabelColumnWidth,
          'shrink-0 relative px-[13.5px] border-r-[0.5px] border-main-200 flex items-start min-w-0',
          isEditable && isUserHovered && 'bg-negative'
        )}
        onMouseEnter={isEditable ? () => setIsUserHovered(true) : undefined}
        onMouseLeave={isEditable ? () => setIsUserHovered(false) : undefined}
      >
        <div className="flex items-center gap-x-f1 min-w-0 h-[2lh]">
          {user ? (
            <RoleColorDot role={user.role} />
          ) : (
            <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
          )}
          {onUserClick ? (
            <button
              type="button"
              className="text-main text-xmd truncate hover:underline cursor-pointer"
              onClick={() => onUserClick(userId)}
            >
              {userId || '—'}
            </button>
          ) : (
            <span className="text-main text-xmd truncate">{userId || '—'}</span>
          )}
        </div>
        {onAddForUser && (
          <button
            type="button"
            className={clsx(
              'group absolute top-0 right-0 flex items-center justify-center w-[2lh] min-w-[2lh] h-[2lh] overflow-hidden border-l-[0.5px] border-main-200 text-main-500 hover:bg-negative hover:text-main cursor-pointer font-FragmentMono text-xmd',
              items.length > 1 && 'border-b-[0.5px]'
            )}
            onClick={() => onAddForUser(userId)}
            data-testid={`add-item-for-user-${userId}`}
          >
            <AnimatedGradientBg />
            <span className="relative">+</span>
          </button>
        )}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        {items.map((item, i) => (
          <ItemRow
            key={item.id}
            item={item}
            index={startIndex + i}
            isLast={
              i === items.length - 1 &&
              !(balances && balances.length > 0 && items.length > 1)
            }
            onEdit={onEdit ? () => onEdit(item.id) : undefined}
            userHovered={isUserHovered}
            onItemClick={onItemClick}
          />
        ))}
        {balances && balances.length > 0 && items.length > 1 && (
          <div
            className={clsx(
              'flex flex-row justify-end items-center gap-x-f1 h-f4 px-[13.5px]',
              isEditable && isUserHovered && 'bg-negative'
            )}
          >
            {balances.map((balance) => {
              const amt = Number(balance.net.expected);
              return (
                <FormatBillingAmount
                  key={balance.currency}
                  amount={amt}
                  currencyPrecision={
                    currencyPrecisionMap[balance.currency as CurrencyCode] || 2
                  }
                  currencyCode={balance.currency as CurrencyCode}
                  secondary
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DraftItemRow = ({
  users,
  initialValues,
  onSubmit,
  onUpdate,
  onCancel,
  onRemove,
}: {
  users: User[];
  initialValues: ItemTableItem | null;
  onSubmit: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdate: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onCancel: () => void;
  onRemove?: () => void;
}) => {
  const [itemType, setItemType] = useState<InvoiceLineItemType>(
    initialValues?.type || 'payin'
  );
  const isEdit = initialValues !== null;
  const [productCode, setProductCode] = useState(
    isEdit ? initialValues.product_id : ''
  );
  const [userId, setUserId] = useState(isEdit ? initialValues.user_id : '');
  const [description, setDescription] = useState(
    isEdit ? initialValues.description : ''
  );
  const [currencyCode, setCurrencyCode] = useState<string>(
    isEdit ? initialValues.currencyCode : 'USD'
  );
  const precision = currencyPrecisionMap[currencyCode as CurrencyCode] || 2;
  const [amount, setAmount] = useState(
    isEdit ? centsToDollars(initialValues.amount, precision) : ''
  );
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  const handleProductChange = useCallback((code: string) => {
    setProductCode(code);
    setUserId('');
  }, []);

  const handleProductCreated = useCallback(
    (code: string) => {
      handleProductChange(code);
      setShowCreateProductModal(false);
    },
    [handleProductChange]
  );

  const handleUserCreated = useCallback((externalId: string) => {
    setUserId(externalId);
    setShowCreateUserModal(false);
  }, []);

  const handleSubmit = useCallback(() => {
    const item: Omit<ItemTableItem, 'id'> = {
      type: itemType,
      product_id: productCode,
      user_id: userId,
      amount: dollarsToCents(amount, precision),
      currencyCode: currencyCode as CurrencyCode,
      description,
    };
    if (isEdit) {
      onUpdate(initialValues.id, item);
    } else {
      onSubmit(item);
    }
    onCancel();
  }, [
    itemType,
    productCode,
    userId,
    currencyCode,
    description,
    amount,
    precision,
    isEdit,
    initialValues,
    onSubmit,
    onUpdate,
    onCancel,
  ]);

  const canSubmit =
    productCode.trim() !== '' &&
    userId.trim() !== '' &&
    amount.trim() !== '' &&
    !Number.isNaN(Number(stripCommas(amount)));

  const hasChanges = isEdit
    ? itemType !== initialValues.type ||
      productCode !== initialValues.product_id ||
      userId !== initialValues.user_id ||
      description !== initialValues.description ||
      dollarsToCents(amount, precision) !== initialValues.amount ||
      currencyCode !== initialValues.currencyCode
    : true;

  const selectedUser = users.find((u) => u.external_id === userId);

  return (
    <>
      <div className="flex flex-col w-full text-xmd font-helvetica">
        {/* User summary */}
        <div className="flex flex-row items-center px-f1 h-f4 border-[0.5px] border-main-200">
          <div className="flex items-center gap-x-f1">
            {selectedUser ? (
              <RoleColorDot role={selectedUser.role} />
            ) : (
              <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
            )}
            <span className="text-main">{userId || 'No user'}</span>
          </div>
        </div>

        {/* Product summary */}
        <div className="flex flex-row items-center px-f1 h-f4 border-[0.5px] border-t-0 border-main-200">
          <div className="flex items-center gap-x-f1">
            <ItemColorSquare productId={productCode} />
            <span className="text-main">{productCode || 'No product'}</span>
          </div>
        </div>

        {/* Direction */}
        <div className="flex flex-row items-stretch h-f4 mt-f1">
          <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-[0.5px] border-b-0 border-main-200 flex items-center">
            Direction
          </span>
          <button
            type="button"
            className={clsx(
              'flex items-center gap-x-[0.5ch] cursor-pointer font-FragmentMono px-f1 border-[0.5px] border-l-0 border-b-0 border-main-200',
              itemType === 'payin'
                ? 'bg-main-100 text-main'
                : 'text-main-500 hover:bg-negative'
            )}
            onClick={() => setItemType('payin')}
          >
            <span>↙</span>
            <span className="font-helvetica">Payin</span>
          </button>
          <button
            type="button"
            className={clsx(
              'flex items-center gap-x-[0.5ch] cursor-pointer font-FragmentMono px-f1 border-[0.5px] border-l-0 border-b-0 border-main-200',
              itemType === 'payout'
                ? 'bg-main-100 text-main'
                : 'text-main-500 hover:bg-negative'
            )}
            onClick={() => setItemType('payout')}
          >
            <span>↗</span>
            <span className="font-helvetica">Payout</span>
          </button>
        </div>

        {/* Description */}
        <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-main-200">
          <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
            Description
          </span>
          <div className="flex-1 flex items-stretch bg-main-100">
            <TextInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              size="xmd"
              width="w-full"
              height="h-full"
              padding="px-f1"
              align="left"
              background="!bg-main-100"
              extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
              data-testid="draft-item-description-input"
            />
          </div>
        </div>

        {/* Quantity */}
        <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
          <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
            Quantity
          </span>
          <div className="flex-1 flex items-center px-f1 bg-main-100">
            <span className="text-xmd text-main">1</span>
          </div>
        </div>

        {/* Amount */}
        <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
          <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
            Amount
          </span>
          <div className="flex-1 flex items-stretch bg-main-100">
            <TextInput
              value={amount}
              onChange={(e) => setAmount(formatAmountInput(e.target.value))}
              onBlur={() => {
                const num = parseFloat(stripCommas(amount));
                if (!Number.isNaN(num)) {
                  setAmount(formatAmountInput(num.toFixed(precision)));
                }
              }}
              placeholder="0.00"
              size="xmd"
              width="w-full"
              height="h-full"
              padding="px-f1"
              align="left"
              background="!bg-main-100"
              extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
              data-testid="draft-item-amount-input"
            />
          </div>
        </div>

        {/* Currency */}
        <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
          <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
            Currency
          </span>
          <div className="relative flex-1 flex items-center bg-main-100">
            <Dropdown
              options={currencyCodes
                .filter((code) => code !== 'LOGICAL' && code !== 'CUSTOM')
                .map((code) => ({ value: code, label: code }))}
              onChange={setCurrencyCode}
              displayValue={currencyCode}
              overrideDisplayName={
                <span className="flex items-center w-full">
                  <span>{currencyCode}</span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              }
              showIcon={false}
              backgroundColor="bg-main-100"
              hoverBackgroundColor="bg-negative"
              shadow="shadow-lg"
              size="xmd"
              width="w-full"
              height="h-f4"
              padding="px-f1"
              align="left"
              data-testid="draft-item-currency-dropdown"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-row items-center pt-f2">
          <div className="flex flex-row gap-x-[0.25lh]">
            <SecondaryButtonV3
              onClick={onCancel}
              className="!text-xmd"
              data-testid="draft-item-cancel-button"
            >
              Cancel
            </SecondaryButtonV3>
            <PrimaryButtonV3
              onClick={handleSubmit}
              disabled={!canSubmit || !hasChanges}
              data-testid="draft-item-submit-button"
            >
              {isEdit ? 'Save' : 'Add'}
            </PrimaryButtonV3>
          </div>
          {isEdit && onRemove && (
            <button
              type="button"
              className="ml-auto flex items-center justify-center p-[4px] px-[9px] font-helvetica text-xmd rounded-[2px] border-[0.5px] border-liability-red text-liability-red cursor-pointer hover:bg-negative transition-colors"
              onClick={onRemove}
              data-testid="draft-item-delete-button"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      {showCreateProductModal && (
        <CreateProductModal
          onClose={() => setShowCreateProductModal(false)}
          onCreated={handleProductCreated}
        />
      )}
      {showCreateUserModal && (
        <CreateUserModal
          onClose={() => setShowCreateUserModal(false)}
          onCreated={handleUserCreated}
        />
      )}
    </>
  );
};

type WizardStep =
  | 'user'
  | 'createUser'
  | 'product'
  | 'createProduct'
  | 'details';

/** Static square badge matching the Catalog nav icon gradient. */
const NavStyleSquare = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    className="shrink-0"
  >
    <defs>
      <radialGradient id="nav-square-rg" cx="55%" cy="45%" r="70%">
        <stop offset="0%" stopColor="rgb(var(--colour-main-800))" />
        <stop offset="100%" stopColor="rgb(var(--colour-main))" />
      </radialGradient>
    </defs>
    <rect width={size} height={size} rx={2} ry={2} fill="url(#nav-square-rg)" />
  </svg>
);

/** Static circle badge matching the Users nav icon gradient (main-300 → main-200). */
const NavStyleCircle = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    className="shrink-0"
  >
    <defs>
      <radialGradient id="nav-circle-rg" cx="45%" cy="55%" r="60%">
        <stop offset="0%" stopColor="rgb(var(--colour-main-800))" />
        <stop offset="100%" stopColor="rgb(var(--colour-main))" />
      </radialGradient>
    </defs>
    <circle
      cx={size / 2}
      cy={size / 2}
      r={size / 2}
      fill="url(#nav-circle-rg)"
    />
  </svg>
);

const CreateUserStep = ({
  externalId,
  setExternalId,
  role,
  setRole,
  onCreated,
}: {
  externalId: string;
  setExternalId: (v: string) => void;
  role: string;
  setRole: (v: string) => void;
  onCreated: (externalId: string) => void;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postUserMutation = usePostUsers();
  const { data: rolesData } = useGetRoles();
  const roles: Role[] = rolesData?.data ?? [];

  const roleOptions = roles.map((r) => ({
    value: r.role,
    label: (
      <span className="flex items-center gap-x-f1">
        <RoleColorDot role={r.role} />
        <span>{r.role}</span>
      </span>
    ),
  }));

  const canSubmit =
    externalId.trim() !== '' &&
    role.trim() !== '' &&
    !postUserMutation.isPending;

  const handleCreate = async () => {
    try {
      await postUserMutation.mutateAsync({
        data: { external_id: externalId.trim(), role },
      });
      await queryClient.invalidateQueries({ queryKey: ['/users'] });
      showToast(<Toast type="success" message="User created" />);
      onCreated(externalId.trim());
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create user.';
      showToast(<Toast type="error" message={message} />);
    }
  };

  return (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      {/* External ID */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          External ID
        </span>
        <div className="flex-1 flex items-stretch bg-main-100">
          <TextInput
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder="user_123"
            size="xmd"
            width="w-full"
            height="h-full"
            padding="px-f1"
            align="left"
            background="!bg-main-100"
            extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
            data-testid="create-user-external-id-input"
          />
        </div>
      </div>

      {/* Role */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Role
        </span>
        <div className="relative flex-1 flex items-center bg-main-100">
          <Dropdown
            options={roleOptions}
            onChange={setRole}
            displayValue={role}
            placeholder="Select Option"
            showIcon={false}
            overrideDisplayName={
              role ? (
                <span className="flex items-center w-full">
                  <span className="flex items-center gap-x-f1">
                    <RoleColorDot role={role} />
                    <span>{role}</span>
                  </span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              ) : (
                <span className="flex items-center w-full">
                  <span className="text-main-500">Select Option</span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              )
            }
            backgroundColor="bg-main-100"
            hoverBackgroundColor="bg-negative"
            shadow="shadow-lg"
            size="xmd"
            width="w-full"
            height="h-f4"
            padding="px-f1"
            align="left"
            data-testid="create-user-role-dropdown"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-row gap-x-[0.25lh] pt-f2">
        <PrimaryButtonV3
          onClick={handleCreate}
          disabled={!canSubmit}
          data-testid="create-user-submit-button"
        >
          {postUserMutation.isPending ? 'Creating...' : 'Create'}
        </PrimaryButtonV3>
      </div>
    </div>
  );
};

const CreateProductStep = ({
  code,
  setCode,
  productDescription,
  setProductDescription,
  paidByRole,
  setPaidByRole,
  paidToRole,
  setPaidToRole,
  onCreated,
}: {
  code: string;
  setCode: (v: string) => void;
  productDescription: string;
  setProductDescription: (v: string) => void;
  paidByRole: string;
  setPaidByRole: (v: string) => void;
  paidToRole: string;
  setPaidToRole: (v: string) => void;
  onCreated: (productCode: string) => void;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postProductMutation = usePostProducts();
  const { data: rolesData } = useGetRoles();
  const roles: Role[] = rolesData?.data ?? [];

  const roleOptions = roles.map((r) => ({
    value: r.role,
    label: (
      <span className="flex items-center gap-x-f1">
        <RoleColorDot role={r.role} />
        <span>{r.role}</span>
      </span>
    ),
  }));

  const hasAtLeastOneRole =
    paidByRole.trim() !== '' || paidToRole.trim() !== '';
  const canSubmit =
    code.trim() !== '' && hasAtLeastOneRole && !postProductMutation.isPending;

  const handleCreate = async () => {
    try {
      await postProductMutation.mutateAsync({
        data: {
          code: code.trim(),
          ...(productDescription.trim() && {
            description: productDescription.trim(),
          }),
          ...(paidByRole && { paid_by_roles: [{ name: paidByRole }] }),
          ...(paidToRole && { paid_to_roles: [{ name: paidToRole }] }),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/products'] });
      showToast(<Toast type="success" message="Product created" />);
      onCreated(code.trim());
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create product.';
      showToast(<Toast type="error" message={message} />);
    }
  };

  return (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      {/* Code */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Code
        </span>
        <div className="flex-1 flex items-stretch bg-main-100">
          <TextInput
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="product_code"
            size="xmd"
            width="w-full"
            height="h-full"
            padding="px-f1"
            align="left"
            background="!bg-main-100"
            extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
            data-testid="create-product-code-input"
          />
        </div>
      </div>

      {/* Description */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Description
        </span>
        <div className="flex-1 flex items-stretch bg-main-100">
          <TextInput
            value={productDescription}
            onChange={(e) => setProductDescription(e.target.value)}
            placeholder="Optional"
            size="xmd"
            width="w-full"
            height="h-full"
            padding="px-f1"
            align="left"
            background="!bg-main-100"
            extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
            data-testid="create-product-description-input"
          />
        </div>
      </div>

      {/* Paid By Role */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Paid By
        </span>
        <div className="relative flex-1 flex items-center bg-main-100">
          <Dropdown
            options={roleOptions}
            onChange={setPaidByRole}
            displayValue={paidByRole}
            placeholder="Select Option"
            showIcon={false}
            overrideDisplayName={
              paidByRole ? (
                <span className="flex items-center w-full">
                  <span className="flex items-center gap-x-f1">
                    <RoleColorDot role={paidByRole} />
                    <span>{paidByRole}</span>
                  </span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              ) : (
                <span className="flex items-center w-full">
                  <span className="text-main-500">Select Option</span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              )
            }
            backgroundColor="bg-main-100"
            hoverBackgroundColor="bg-negative"
            shadow="shadow-lg"
            size="xmd"
            width="w-full"
            height="h-f4"
            padding="px-f1"
            align="left"
            data-testid="create-product-paid-by-dropdown"
          />
        </div>
      </div>

      {/* Paid To Role */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Paid To
        </span>
        <div className="relative flex-1 flex items-center bg-main-100">
          <Dropdown
            options={roleOptions}
            onChange={setPaidToRole}
            displayValue={paidToRole}
            placeholder="Select Option"
            showIcon={false}
            overrideDisplayName={
              paidToRole ? (
                <span className="flex items-center w-full">
                  <span className="flex items-center gap-x-f1">
                    <RoleColorDot role={paidToRole} />
                    <span>{paidToRole}</span>
                  </span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              ) : (
                <span className="flex items-center w-full">
                  <span className="text-main-500">Select Option</span>
                  <span className="font-FragmentMono text-main-500 ml-auto">
                    ↓
                  </span>
                </span>
              )
            }
            backgroundColor="bg-main-100"
            hoverBackgroundColor="bg-negative"
            shadow="shadow-lg"
            size="xmd"
            width="w-full"
            height="h-f4"
            padding="px-f1"
            align="left"
            data-testid="create-product-paid-to-dropdown"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-row gap-x-[0.25lh] pt-f2">
        <PrimaryButtonV3
          onClick={handleCreate}
          disabled={!canSubmit}
          data-testid="create-product-submit-button"
        >
          {postProductMutation.isPending ? 'Creating...' : 'Create'}
        </PrimaryButtonV3>
      </div>
    </div>
  );
};

const AddItemWizardModal = ({
  users,
  products,
  existingUserIds,
  initialUserId,
  onSubmit,
  onClose,
}: {
  users: User[];
  products: Product[];
  existingUserIds: Set<string>;
  initialUserId?: string;
  onSubmit: (item: Omit<ItemTableItem, 'id'>) => void;
  onClose: () => void;
}) => {
  const [step, setStep] = useState<WizardStep>(
    initialUserId ? 'product' : 'user'
  );
  const [selectedUserId, setSelectedUserId] = useState(initialUserId ?? '');
  const [selectedProductCode, setSelectedProductCode] = useState('');
  const [itemType, setItemType] = useState<InvoiceLineItemType>('payin');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [newUserExternalId, setNewUserExternalId] = useState('');
  const [newUserRole, setNewUserRole] = useState('');
  const [newProductCode, setNewProductCode] = useState('');
  const [newProductDescription, setNewProductDescription] = useState('');
  const [newProductPaidByRole, setNewProductPaidByRole] = useState('');
  const [newProductPaidToRole, setNewProductPaidToRole] = useState('');

  const availableUsers = users.filter(
    (u) => !existingUserIds.has(u.external_id)
  );

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setStep('product');
  };

  const handleProductSelect = (code: string) => {
    setSelectedProductCode(code);
    const product = products.find((p) => p.code === code);
    if (product?.description) setDescription(product.description);
    setStep('details');
  };

  const wizardPrecision =
    currencyPrecisionMap[currencyCode as CurrencyCode] || 2;
  const canSubmit =
    amount.trim() !== '' && !Number.isNaN(Number(stripCommas(amount)));

  const handleSubmit = () => {
    onSubmit({
      type: itemType,
      product_id: selectedProductCode,
      user_id: selectedUserId,
      amount: dollarsToCents(amount, wizardPrecision),
      currencyCode: currencyCode as CurrencyCode,
      description,
    });
    onClose();
  };

  const handleBack = () => {
    if (step === 'details') setStep('product');
    else if (step === 'createProduct') setStep('product');
    else setStep('user');
  };

  const backButton = (
    <button
      type="button"
      className="absolute left-f1 top-1/2 -translate-y-1/2 font-FragmentMono text-main-500 hover:text-main cursor-pointer text-xmd"
      onClick={handleBack}
    >
      ←
    </button>
  );

  const renderHeader = (title: string, showBack: boolean) => (
    <div className="relative flex items-center justify-center w-full text-main text-xmd font-helvetica">
      {showBack && backButton}
      <span>{title}</span>
    </div>
  );

  const renderUserStep = () => (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      <div className="border-[0.5px] border-main-200">
        {/* New User row */}
        <button
          type="button"
          className={clsx(
            'flex flex-row items-center justify-between px-f1 h-f4 w-full bg-main-100 hover:bg-negative cursor-pointer',
            'border-b-[0.5px] border-main-200'
          )}
          onClick={() => setStep('createUser')}
          data-testid="add-user-option-new"
        >
          <div className="flex items-center gap-x-f1">
            <NavStyleCircle />
            <span className="text-main">New User</span>
          </div>
          <span className="text-main-500">+</span>
        </button>
        {availableUsers.map((user, i) => (
          <button
            key={user.external_id}
            type="button"
            className={clsx(
              'flex flex-row items-center justify-between px-f1 h-f4 w-full hover:bg-negative cursor-pointer',
              i < availableUsers.length - 1 &&
                'border-b-[0.5px] border-main-200'
            )}
            onClick={() => handleUserSelect(user.external_id)}
            data-testid={`add-user-option-${user.external_id}`}
          >
            <div className="flex items-center gap-x-f1">
              <RoleColorDot role={user.role} />
              <span className="text-main">{user.external_id}</span>
            </div>
            <span className="text-main-500">+</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderProductStep = () => {
    const userForBadge = users.find((u) => u.external_id === selectedUserId);
    const userRole = userForBadge?.role;
    const roleProducts = userRole
      ? products.filter(
          (p) =>
            p.paid_by_roles.some((r) => r.name === userRole) ||
            p.paid_to_roles.some((r) => r.name === userRole)
        )
      : products;
    return (
      <div className="flex flex-col w-full text-xmd font-helvetica">
        {/* Selected User */}
        <div className="flex flex-row items-center justify-between px-f1 h-f4 border-[0.5px] border-main-200">
          <div className="flex items-center gap-x-f1">
            {userForBadge ? (
              <RoleColorDot role={userForBadge.role} />
            ) : (
              <NavStyleCircle />
            )}
            <span className="text-main">{selectedUserId}</span>
          </div>
        </div>
        {/* New Item row */}
        <button
          type="button"
          className={clsx(
            'flex flex-row items-center justify-between px-f1 h-f4 w-full bg-main-100 hover:bg-negative cursor-pointer',
            'border-[0.5px] border-main-200 mt-f1'
          )}
          onClick={() => setStep('createProduct')}
          data-testid="add-product-option-new"
        >
          <div className="flex items-center gap-x-f1">
            <NavStyleSquare />
            <span className="text-main">New Item</span>
          </div>
          <span className="text-main-500">+</span>
        </button>
        {roleProducts.map((product, i) => (
          <button
            key={product.code}
            type="button"
            className={clsx(
              'flex flex-row items-center justify-between px-f1 h-f4 w-full hover:bg-negative cursor-pointer',
              'border-[0.5px] border-t-0 border-main-200',
              i === roleProducts.length - 1 && 'border-b-[0.5px]'
            )}
            onClick={() => handleProductSelect(product.code)}
            data-testid={`add-product-option-${product.code}`}
          >
            <div className="flex items-center gap-x-f1">
              <ItemColorSquare productId={product.code} />
              <span className="text-main">{product.code}</span>
            </div>
            <span className="text-main-500">+</span>
          </button>
        ))}
      </div>
    );
  };

  const selectedUser = users.find((u) => u.external_id === selectedUserId);

  const detailsBody = (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      {/* Selected User */}
      <div className="flex flex-row items-center justify-between px-f1 h-f4 border-[0.5px] border-main-200">
        <div className="flex items-center gap-x-f1">
          {selectedUser ? (
            <RoleColorDot role={selectedUser.role} />
          ) : (
            <NavStyleCircle />
          )}
          <span className="text-main">{selectedUserId}</span>
        </div>
      </div>
      {/* Selected Product */}
      <div className="flex flex-row items-center justify-between px-f1 h-f4 border-[0.5px] border-t-0 border-main-200">
        <div className="flex items-center gap-x-f1">
          <ItemColorSquare productId={selectedProductCode} />
          <span className="text-main">{selectedProductCode}</span>
        </div>
      </div>

      {/* Direction */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-main-200 mt-f1">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Direction
        </span>
        <div className="flex flex-row items-stretch">
          <button
            type="button"
            className={clsx(
              'flex items-center gap-x-[0.5ch] cursor-pointer font-FragmentMono px-f1 border-r-[0.5px] border-main-200',
              itemType === 'payin'
                ? 'bg-main-100 text-main'
                : 'text-main-500 hover:bg-negative'
            )}
            onClick={() => setItemType('payin')}
          >
            <span>↙</span>
            <span className="font-helvetica">Payin</span>
          </button>
          <button
            type="button"
            className={clsx(
              'flex items-center gap-x-[0.5ch] cursor-pointer font-FragmentMono px-f1',
              itemType === 'payout'
                ? 'bg-main-100 text-main'
                : 'text-main-500 hover:bg-negative'
            )}
            onClick={() => setItemType('payout')}
          >
            <span>↗</span>
            <span className="font-helvetica">Payout</span>
          </button>
        </div>
      </div>

      {/* Description */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Description
        </span>
        <div className="flex-1 flex items-stretch bg-main-100">
          <TextInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            size="xmd"
            width="w-full"
            height="h-full"
            padding="px-f1"
            align="left"
            background="!bg-main-100"
            extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
            data-testid="wizard-description-input"
          />
        </div>
      </div>

      {/* Quantity */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Quantity
        </span>
        <div className="flex-1 flex items-center px-f1 bg-main-100">
          <span className="text-xmd text-main">1</span>
        </div>
      </div>

      {/* Amount */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Amount
        </span>
        <div className="flex-1 flex items-stretch bg-main-100">
          <TextInput
            value={amount}
            onChange={(e) => setAmount(formatAmountInput(e.target.value))}
            onBlur={() => {
              const num = parseFloat(stripCommas(amount));
              if (!Number.isNaN(num)) {
                setAmount(formatAmountInput(num.toFixed(wizardPrecision)));
              }
            }}
            placeholder="0.00"
            size="xmd"
            width="w-full"
            height="h-full"
            padding="px-f1"
            align="left"
            background="!bg-main-100"
            extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
            data-testid="wizard-amount-input"
          />
        </div>
      </div>

      {/* Currency */}
      <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
        <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
          Currency
        </span>
        <div className="relative flex-1 flex items-center bg-main-100">
          <Dropdown
            options={currencyCodes
              .filter((code) => code !== 'LOGICAL' && code !== 'CUSTOM')
              .map((code) => ({ value: code, label: code }))}
            onChange={setCurrencyCode}
            displayValue={currencyCode}
            overrideDisplayName={
              <span className="flex items-center w-full">
                <span>{currencyCode}</span>
                <span className="font-FragmentMono text-main-500 ml-auto">
                  ↓
                </span>
              </span>
            }
            showIcon={false}
            backgroundColor="bg-main-100"
            hoverBackgroundColor="bg-negative"
            shadow="shadow-lg"
            size="xmd"
            width="w-full"
            height="h-f4"
            padding="px-f1"
            align="left"
            data-testid="wizard-currency-dropdown"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-row gap-x-[0.25lh] pt-f2">
        <SecondaryButtonV3
          onClick={onClose}
          className="!text-xmd"
          data-testid="wizard-cancel-button"
        >
          Cancel
        </SecondaryButtonV3>
        <PrimaryButtonV3
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="wizard-submit-button"
        >
          Add
        </PrimaryButtonV3>
      </div>
    </div>
  );

  const createUserBody = (
    <CreateUserStep
      externalId={newUserExternalId}
      setExternalId={setNewUserExternalId}
      role={newUserRole}
      setRole={setNewUserRole}
      onCreated={(extId) => handleUserSelect(extId)}
    />
  );

  const createProductBody = (
    <CreateProductStep
      code={newProductCode}
      setCode={setNewProductCode}
      productDescription={newProductDescription}
      setProductDescription={setNewProductDescription}
      paidByRole={newProductPaidByRole}
      setPaidByRole={setNewProductPaidByRole}
      paidToRole={newProductPaidToRole}
      setPaidToRole={setNewProductPaidToRole}
      onCreated={(productCode) => handleProductSelect(productCode)}
    />
  );

  const titles: Record<WizardStep, string> = {
    user: 'Add User',
    createUser: 'New User',
    product: 'Select Item',
    createProduct: 'New Item',
    details: 'Product Details',
  };

  const bodies: Record<WizardStep, JSX.Element> = {
    user: renderUserStep(),
    createUser: createUserBody,
    product: renderProductStep(),
    createProduct: createProductBody,
    details: detailsBody,
  };

  return (
    <PaymentsModal
      data-testid="add-item-wizard-modal"
      onClose={onClose}
      width="sm"
      shouldCenter={false}
      header={renderHeader(
        titles[step],
        step !== 'user' && !(step === 'product' && initialUserId)
      )}
      body={bodies[step]}
    />
  );
};

const AddUserRow = ({ onAdd }: { onAdd: () => void }) => (
  <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
    <button
      type="button"
      className={clsx(
        kLabelColumnWidth,
        'shrink-0 px-[13.5px] h-f4 border-r-[0.5px] border-main-200 flex items-center gap-x-f1',
        'group overflow-hidden relative text-main hover:bg-negative cursor-pointer text-xmd'
      )}
      onClick={onAdd}
      aria-label="Add user"
      data-testid="add-new-item-button"
    >
      <AnimatedGradientBg />
      <span className="relative flex items-center gap-x-f1">
        <NavStyleCircle />
        Add User +
      </span>
    </button>
  </div>
);

type ItemTableProps = {
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  onUserClick?: (userId: string) => void;
  onItemClick?: (item: ItemTableItem) => void;
  userBalances?: UserBalances[];
  'data-testid'?: string;
};

export const ItemTable = ({
  items,
  products,
  users,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onUserClick,
  onItemClick,
  userBalances,
  'data-testid': dataTestId,
}: ItemTableProps): JSX.Element => {
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [preselectedUserId, setPreselectedUserId] = useState<
    string | undefined
  >(undefined);
  const [showDraftItem, setShowDraftItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const editingItem =
    editingItemId !== null
      ? items.find((i) => i.id === editingItemId) ?? null
      : null;

  const existingUserIds = useMemo(
    () => new Set(items.map((i) => i.user_id)),
    [items]
  );

  const handleAddItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      onAddItem?.(item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onAddItem]
  );

  const handleUpdateItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      onUpdateItem?.(id, item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onUpdateItem]
  );

  const handleCancelDraft = useCallback(() => {
    setShowDraftItem(false);
    setEditingItemId(null);
  }, []);

  const handleStartEdit = useCallback((id: string) => {
    setEditingItemId(id);
    setShowDraftItem(true);
  }, []);

  const handleStartAdd = useCallback(() => {
    setPreselectedUserId(undefined);
    setShowUserPicker(true);
  }, []);

  const handleAddForUser = useCallback((userId: string) => {
    setPreselectedUserId(userId);
    setShowUserPicker(true);
  }, []);

  const handleWizardSubmit = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      onAddItem?.(item);
      setShowUserPicker(false);
    },
    [onAddItem]
  );

  const sortedItems = items
    .filter((item) => item.id !== editingItemId)
    .sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'payin' ? -1 : 1;
    });

  // Group items by user_id, preserving order of first appearance
  const userGroups: { userId: string; items: ItemTableItem[] }[] = [];
  const seen = new Map<string, ItemTableItem[]>();
  sortedItems.forEach((item) => {
    const uid = item.user_id || '';
    if (!seen.has(uid)) {
      const group: ItemTableItem[] = [];
      seen.set(uid, group);
      userGroups.push({ userId: uid, items: group });
    }
    seen.get(uid)!.push(item);
  });

  // Compute start indices for data-testid continuity
  const groupStartIndices = userGroups.reduce<number[]>((acc, _, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + userGroups[i - 1].items.length);
    return acc;
  }, []);

  return (
    <div data-testid={dataTestId} className="flex flex-col h-fit w-full">
      {userGroups.length > 0 && (
        <div className="flex flex-row w-full items-center border-b-[0.5px] border-main-200 h-f4">
          <span
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 text-main-500 text-xmd px-[13.5px] border-r-[0.5px] border-main-200'
            )}
          >
            Users
          </span>
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main-500 font-FragmentMono text-xmd">
            ↙
          </div>
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main-500 font-FragmentMono text-xmd">
            ↗
          </div>
          <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex-[3] flex items-center">
            Name
          </span>
          <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex-[0.5] flex items-center justify-end">
            Qty
          </span>
          <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex items-center justify-end">
            Price
          </span>
          <span className="text-main-500 text-xmd px-[13.5px] flex-1 basis-0 flex items-center justify-end">
            Due
          </span>
        </div>
      )}
      {userGroups.map(({ userId, items: groupItems }, groupIndex) => (
        <UserGroup
          key={userId}
          userId={userId}
          items={groupItems}
          startIndex={groupStartIndices[groupIndex]}
          balances={userBalances?.find((u) => u.id === userId)?.balances}
          users={users}
          onRemove={onRemoveItem}
          onEdit={onUpdateItem ? handleStartEdit : undefined}
          onUserClick={onUserClick}
          onItemClick={onItemClick}
          onAddForUser={onAddItem ? handleAddForUser : undefined}
        />
      ))}
      {onAddItem && <AddUserRow onAdd={handleStartAdd} />}
      {showUserPicker && onAddItem && (
        <AddItemWizardModal
          users={users}
          products={products}
          existingUserIds={existingUserIds}
          initialUserId={preselectedUserId}
          onSubmit={handleWizardSubmit}
          onClose={() => setShowUserPicker(false)}
        />
      )}
      {showDraftItem && (onAddItem || onUpdateItem) && (
        <PaymentsModal
          data-testid="add-item-modal"
          onClose={handleCancelDraft}
          width="sm"
          header={
            <div className="relative flex items-center justify-center w-full text-main text-xmd font-helvetica">
              <span>{editingItem ? 'Edit Item' : 'Add New Item'}</span>
            </div>
          }
          body={
            <DraftItemRow
              key={editingItemId ?? 'add'}
              users={users}
              initialValues={editingItem}
              onSubmit={handleAddItem}
              onUpdate={handleUpdateItem}
              onCancel={handleCancelDraft}
              onRemove={
                onRemoveItem && editingItem
                  ? () => {
                      onRemoveItem(editingItem.id);
                      handleCancelDraft();
                    }
                  : undefined
              }
            />
          }
        />
      )}
    </div>
  );
};
